import { InformationButton } from "@components/buttons/information";
import { SHButton, SHStack } from "@components/design-systems";
import { useIsNew } from "@hooks/useIsNew";
import { StepperButtonState } from "@layouts/stepper";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { AnalysisSubStep } from "@models/platform-analysis/enums/subStep";
import { AnalysisToolbarButtonsSkeleton } from "@pages/platform-analysis-v2/_id/components/skeletons/analysis-toolbar-buttons";
import { CompleteButton } from "@pages/platform-analysis/components/buttons/complete";
import { ExportPDFButton } from "@pages/platform-analysis/components/buttons/export-pdf";
import { UnlockAnalysisButton } from "@pages/platform-analysis/components/buttons/unlock";
import { RootState } from "@redux/store";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

type AnalysisToolbarButtonsProps = {
  buttonState: StepperButtonState;
  lastedAction: "save" | "next" | "back" | null;
  currentStep?: string;
  currentSubStep?: string;

  handleOnSaveAsTemplate: () => void;
  handleOnExit: () => void;
  handleOnBack: () => void;
  handleOnSave: () => void;
  handleOnNext: () => void;
  handleOnSubmit: () => Promise<unknown>;
};

export const AnalysisToolbarButtons = ({
  buttonState,
  lastedAction,
  currentStep,
  currentSubStep,
  handleOnSaveAsTemplate,
  handleOnExit,
  handleOnBack,
  handleOnSave,
  handleOnNext,
  handleOnSubmit,
}: AnalysisToolbarButtonsProps) => {
  const isNew = useIsNew();
  const { analysisId } = useParams<{ analysisId: string }>();
  const {
    ui: {
      isDirty,
      isValid,
      isSubmitting,
      isValidating,
      isLoadingSteps,
      portfolioLevel,
    },
    isCompleted,
    isReadOnly,
    internal: {
      feeCalculator: { validatedPortfolioSetupFirstTime },
    },
  } = useSelector((state: RootState) => state.platformAnalysis);
  const {
    featureTemplateUI: { isLoading: isLoadingTemplate },
  } = useSelector((state: RootState) => state.featureTemplate);

  const showSaveAsTemplateButton = useMemo(() => {
    return currentStep === AnalysisStep.Feature && !isCompleted;
  }, [currentStep, isCompleted]);

  const isValidatedPortfolioSetupFirstTime = useMemo(() => {
    if (
      currentStep !== AnalysisStep.Fee ||
      currentSubStep === AnalysisSubStep.Analysis ||
      !portfolioLevel ||
      portfolioLevel === FeeCalculatorPortfolioLevel.No
    )
      return true;
    return validatedPortfolioSetupFirstTime;
  }, [
    currentStep,
    currentSubStep,
    portfolioLevel,
    validatedPortfolioSetupFirstTime,
  ]);

  if (isLoadingSteps) return <AnalysisToolbarButtonsSkeleton />;

  return (
    <SHStack
      direction="row"
      justifyContent="flex-start"
      alignItems={"center"}
      spacing={"15px"}
    >
      {showSaveAsTemplateButton && (
        <SHButton
          variant="outlined"
          size="extraMedium"
          disabled={isSubmitting}
          isLoading={isLoadingTemplate}
          onClick={handleOnSaveAsTemplate}
        >
          Save template
          <InformationButton
            content="Save the features that are selected below as a template. You and your team can load feature selection templates when you set up your analysis."
            placement="bottom-start"
          />
        </SHButton>
      )}

      <SHButton
        variant="outlined"
        size="extraMedium"
        disabled={isSubmitting}
        onClick={handleOnExit}
      >
        Exit
      </SHButton>

      {buttonState.isCanPrevious && (
        <SHButton
          variant="outlined"
          size="extraMedium"
          disabled={isSubmitting || isValidating}
          isLoading={lastedAction === "back" && isSubmitting}
          onClick={handleOnBack}
        >
          Back
        </SHButton>
      )}

      {!isCompleted && (
        <SHButton
          variant="outlined"
          size="extraMedium"
          disabled={
            !isValid ||
            isSubmitting ||
            (!isNew && !isDirty) ||
            isValidating ||
            !isValidatedPortfolioSetupFirstTime
          }
          isLoading={lastedAction === "save" && isSubmitting}
          onClick={handleOnSave}
        >
          Save
        </SHButton>
      )}

      {buttonState.isCanNext ? (
        <SHButton
          variant="contained"
          size="extraMedium"
          disabled={
            !isValid ||
            isSubmitting ||
            isValidating ||
            !isValidatedPortfolioSetupFirstTime
          }
          isLoading={lastedAction === "next" && isSubmitting}
          onClick={handleOnNext}
        >
          Next
        </SHButton>
      ) : isCompleted ? (
        <>
          <UnlockAnalysisButton analysisId={analysisId} />
          <ExportPDFButton isCompleted isPlatformAnalysis />
        </>
      ) : (
        <>
          <CompleteButton
            disabled={isReadOnly}
            analysisId={analysisId}
            onSubmit={handleOnSubmit}
          />
          <ExportPDFButton isCompleted={isCompleted} isPlatformAnalysis />
        </>
      )}
    </SHStack>
  );
};
